import { ContractTemplateHistory } from '@/dsm-components/contract-templates';
import { useLazyGetTemplatesQuery } from '@/services/contractTemplates';
import {
  Box,
  Button,
  Card,
  Column,
  SelectInputNative,
  Table,
  useBreakpoint,
  useOpenClose,
} from '@palmetto/palmetto-components';
import { useCallback, useState } from 'react';
import { Helmet } from 'react-helmet';
import { useSearchParams } from 'react-router-dom';

import {
  AdditionalSignerRole,
  ContractDocumentProviders,
  FinanceTypeLabels,
  Locale,
  OrganizationContractDocumentSettings,
  ProgramType,
  ProgramTypeLabels,
  ProgramTypes,
} from 'types';

import RequireContractAdminUser from '../auth/RequireContractAdminUser';
import { MainContainer } from '../MainContainer';
import PageHeader from '../PageHeader';

import { AddContractModal } from './AddContractModal';
import { ContractFilter } from './ContractFilter';
import { EditContractModal } from './EditContractModal';

const EMPTY_CELL = '—';
const PUERTO_RICO_STATE = 'PR';

const SIGNER_LABELS: Record<AdditionalSignerRole, string> = {
  [AdditionalSignerRole.salesRep]: 'Sales Rep',
  [AdditionalSignerRole.epcInstallerRep]: 'EPC/Installer Rep',
};

const yesNo = (value: boolean | undefined) => (value ? 'Yes' : 'No');

const isCongaRow = (row: OrganizationContractDocumentSettings) =>
  row.contractSettings.contractDocumentSettings?.provider === ContractDocumentProviders.conga;

const providerCell = (row: OrganizationContractDocumentSettings) => (isCongaRow(row) ? 'Conga' : 'DocuSign');

const spanishCell = (row: OrganizationContractDocumentSettings) => {
  if (!isCongaRow(row)) return EMPTY_CELL;
  if (row.contractSettings.state === PUERTO_RICO_STATE) return 'Always';
  return yesNo(row.contractSettings.contractDocumentSettings?.congaSpanishAvailable);
};

const signersCell = (row: OrganizationContractDocumentSettings) => {
  const signers = row.contractSettings.contractDocumentSettings?.requiredAdditionalSigners ?? [];
  return signers.length ? signers.map((role) => SIGNER_LABELS[role] ?? role).join(', ') : EMPTY_CELL;
};

const vaultCell = (row: OrganizationContractDocumentSettings) =>
  isCongaRow(row) ? yesNo(row.contractSettings.contractDocumentSettings?.vaultOnCompletion) : EMPTY_CELL;

const lenderCell = (row: OrganizationContractDocumentSettings) => {
  const settings = row.contractSettings.contractDocumentSettings;
  return isCongaRow(row) && settings?.vaultOnCompletion && settings.eOriginalLender
    ? settings.eOriginalLender
    : EMPTY_CELL;
};

const NotUsedLabel = () => (
  <Box fontSize="sm" fontColor="subdued">
    Not used
  </Box>
);

interface RowActionButtonProps {
  row: OrganizationContractDocumentSettings;
  onSelect: (row: OrganizationContractDocumentSettings) => void;
}

const EditRowButton = ({ row, onSelect }: RowActionButtonProps) => {
  const handleClick = useCallback(() => onSelect(row), [onSelect, row]);
  return (
    <Button
      onClick={handleClick}
      iconPrefix="settings"
      variant="secondary"
      tone="neutral"
      aria-label="edit contract"
    />
  );
};

const HistoryRowButton = ({ row, onSelect }: RowActionButtonProps) => {
  const handleClick = useCallback(() => onSelect(row), [onSelect, row]);
  return (
    <Button
      onClick={handleClick}
      iconPrefix="time-clock"
      variant="secondary"
      tone="neutral"
      aria-label="view history"
    />
  );
};

export function ContractTemplates() {
  const [selected, setSelected] = useState<OrganizationContractDocumentSettings | null>(null);
  const [historyId, setHistoryId] = useState<string | null>(null);
  const { isPhone } = useBreakpoint();
  const [queryParams, setQueryParams] = useSearchParams();

  const advancedFilters = queryParams.get('advancedFilters');
  const filters = advancedFilters ? JSON.parse(advancedFilters) : [];
  const programTypeFilter = filters.find((f: any) => f.id === 'programType');
  const programType = programTypeFilter?.selectedValues[0] as ProgramType;

  if (!programType) {
    setQueryParams({
      advancedFilters: JSON.stringify([
        {
          id: 'programType',
          operation: 'Equal',
          selectedValues: [ProgramType.solar],
        },
      ]),
    });
  }

  const availableProgramTypes = ProgramTypes.map((programType) => ({
    label: ProgramTypeLabels[programType],
    value: programType,
  }));

  const [trigger, { data: templateData = [], isFetching: isTemplateDataFetching }] = useLazyGetTemplatesQuery();
  const {
    isOpen: isAddContractModalOpen,
    handleClose: handleAddContractModalClose,
    handleOpen: handleOpenAddContractModal,
  } = useOpenClose();
  const {
    isOpen: isEditContractModalOpen,
    handleClose: handleEditContractModalClose,
    handleOpen: handleOpenEditContractModal,
  } = useOpenClose();

  const handleClick = useCallback(
    (row: OrganizationContractDocumentSettings) => {
      setSelected(row);
      handleOpenEditContractModal();
    },
    [handleOpenEditContractModal],
  );

  const handleHistoryClick = useCallback((row: OrganizationContractDocumentSettings) => {
    setHistoryId(row.contractSettings.contractDocumentSettingsId?.toString() ?? null);
  }, []);

  const handleHistoryClose = useCallback(() => setHistoryId(null), []);

  const columnConfig = [
    {
      heading: 'State',
      render: (_cell: any, row: OrganizationContractDocumentSettings) => (
        <Box direction="row" alignItems="center">
          {row.contractSettings.state}
        </Box>
      ),
    },
    {
      heading: 'LSE ID',
      render: (_cell: any, row: OrganizationContractDocumentSettings) => (
        <Box direction="row" alignItems="center">
          {row.contractSettings.lseId ?? ''}
        </Box>
      ),
    },
    {
      heading: 'Contract Type',
      render: (_cell: any, row: OrganizationContractDocumentSettings) => (
        <Box direction="row" alignItems="center">
          {FinanceTypeLabels[row.contractSettings.type]}
        </Box>
      ),
    },
    {
      heading: 'Provider',
      render: (_cell: any, row: OrganizationContractDocumentSettings) => providerCell(row),
    },
    {
      heading: 'Spanish',
      render: (_cell: any, row: OrganizationContractDocumentSettings) => spanishCell(row),
    },
    {
      heading: 'Additional Signers',
      render: (_cell: any, row: OrganizationContractDocumentSettings) => signersCell(row),
    },
    {
      heading: 'Vault',
      render: (_cell: any, row: OrganizationContractDocumentSettings) => vaultCell(row),
    },
    {
      heading: 'Lender',
      render: (_cell: any, row: OrganizationContractDocumentSettings) => lenderCell(row),
    },
    {
      heading: 'English Template',
      width: 420,
      render: (_cell: any, row: OrganizationContractDocumentSettings) => {
        const templates = row.contractSettings.contractDocumentSettings?.templates?.filter(
          (t) => t.language === Locale.en,
        );
        const singleTemplate = templates?.find((t) => !t.coborrower);
        const coborrowerTemplate = templates?.find((t) => t.coborrower);
        return (
          <Box childGap="2xs">
            {isCongaRow(row) && <NotUsedLabel />}
            <Box fontSize="sm">Single: {singleTemplate?.templateId ?? '—'}</Box>
            <Box fontSize="sm">Co-borrower: {coborrowerTemplate?.templateId ?? '—'}</Box>
          </Box>
        );
      },
    },
    {
      heading: 'Spanish Template',
      width: 420,
      render: (_cell: any, row: OrganizationContractDocumentSettings) => {
        const templates = row.contractSettings.contractDocumentSettings?.templates?.filter(
          (t) => t.language === Locale.es,
        );
        if (!templates || templates.length === 0) {
          return null;
        }
        const singleTemplate = templates.find((t) => !t.coborrower);
        const coborrowerTemplate = templates.find((t) => t.coborrower);
        return (
          <Box childGap="2xs">
            {isCongaRow(row) && <NotUsedLabel />}
            {singleTemplate && <Box fontSize="sm">Single: {singleTemplate.templateId}</Box>}
            {coborrowerTemplate && <Box fontSize="sm">Co-borrower: {coborrowerTemplate.templateId}</Box>}
          </Box>
        );
      },
    },
    {
      render: (_cell: any, row: OrganizationContractDocumentSettings) => (
        <Box direction="row" gap="xs">
          <HistoryRowButton row={row} onSelect={handleHistoryClick} />
          <RequireContractAdminUser>
            <EditRowButton row={row} onSelect={handleClick} />
          </RequireContractAdminUser>
        </Box>
      ),
    },
  ];

  const handleProgramTypeChange = useCallback(
    (e: React.ChangeEvent<HTMLSelectElement>) => {
      // When selecting a program type it overrides all other filters
      setQueryParams({
        advancedFilters: JSON.stringify([
          {
            id: 'programType',
            operation: 'Equal',
            selectedValues: [e.target.value],
          },
        ]),
      });
    },
    [setQueryParams],
  );

  return (
    <>
      <AddContractModal
        isOpen={isAddContractModalOpen}
        handleClose={handleAddContractModalClose}
        programType={programType}
      />
      <EditContractModal
        isOpen={isEditContractModalOpen}
        handleClose={handleEditContractModalClose}
        template={selected}
      />
      {historyId && <ContractTemplateHistory contractDocumentSettingsId={historyId} onClose={handleHistoryClose} />}
      <Helmet>
        <title>Contracts</title>
      </Helmet>
      <MainContainer>
        <PageHeader title="Contracts"></PageHeader>
        <Box display="flex" direction="row" width="240px">
          <SelectInputNative
            label="Program Type"
            name="programType"
            id="programType"
            options={availableProgramTypes}
            onChange={handleProgramTypeChange}
            value={programType}
            autoComplete="off"
            isDisabled={isTemplateDataFetching}
          />
        </Box>
        <Card padding="sm">
          <Card.Header>
            <Box direction="row" justifyContent="flex-end">
              <RequireContractAdminUser>
                <Button
                  as="button"
                  variant="secondary"
                  iconPrefix="add"
                  size={{ base: 'sm', desktop: 'md' }}
                  aria-label="create template"
                  onClick={handleOpenAddContractModal}
                >
                  {!isPhone && 'Contract'}
                </Button>
              </RequireContractAdminUser>
            </Box>
          </Card.Header>
          <ContractFilter trigger={trigger} />
          {!isTemplateDataFetching && templateData.length === 0 ? (
            <Box padding="xl" textAlign="center">
              No templates found
            </Box>
          ) : (
            <Table
              rowKey="key"
              columns={columnConfig as Column[]}
              rows={isTemplateDataFetching ? [] : templateData}
              isLoading={isTemplateDataFetching}
              isScrollable={{ x: true }}
            />
          )}
        </Card>
      </MainContainer>
    </>
  );
}
